import { MutableRefObject, useEffect, useRef } from "react";

export const useIntersectionObserver = (
  onIntersect: (element: HTMLElement) => void,
  options?: IntersectionObserverInit,
): ((element: HTMLElement) => void) => {
  const observer: MutableRefObject<IntersectionObserver | null> = useRef<IntersectionObserver | null>(null);
  const elementsRef: MutableRefObject<HTMLElement[]> = useRef<HTMLElement[]>([]);

  useEffect(() => {
    if (!observer.current) {
      observer.current = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            onIntersect(entry.target as HTMLElement);
            observer.current?.unobserve(entry.target);
          }
        });
      }, options);
    }

    const { current: currentObserver } = observer;

    elementsRef.current.forEach((el) => currentObserver?.observe(el));

    return () => {
      elementsRef.current.forEach((el) => currentObserver?.unobserve(el));
      currentObserver?.disconnect();
    };
  }, [onIntersect, options]);

  const addElement = (element: HTMLElement) => {
    if (element && !elementsRef.current.includes(element)) {
      elementsRef.current.push(element);
    }
  };

  return addElement;
};
